import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquare, Send, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { ChatMessageMenu } from '@/components/chat/chat-message-menu';

const PAGE_SIZE = 30;

interface Message {
  id: string;
  user_id: string;
  samen_id: string;
  content: string;
  created_at: string;
  meditation_id?: string | null;
  user_name?: string;
  avatar_url?: string;
}

interface SamenChatProps {
  samenId: string;
  meditationId?: string | null;
  onlineCount?: number;
}

export function SamenChat({ samenId, meditationId, onlineCount }: SamenChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { profile } = useProfile();

  const fetchMessages = useCallback(async (before?: string) => {
    if (!samenId) return;

    const isInitial = !before;
    if (isInitial) setLoading(true);
    else setLoadingOlder(true);

    try {
      let query = supabase
        .from('samen_messages')
        .select('id, user_id, samen_id, content, meditation_id, created_at')
        .eq('samen_id', samenId)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);

      if (meditationId) {
        query = query.eq('meditation_id', meditationId);
      } else {
        query = query.is('meditation_id', null);
      }

      if (before) {
        query = query.lt('created_at', before);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data && data.length > 0) {
        // Fetch profiles separately to avoid RLS join issues
        const userIds = [...new Set(data.map((msg) => msg.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, naam, avatar_url')
          .in('id', userIds);

        const profileMap = new Map(
          (profiles ?? []).map((p) => [p.id, p]),
        );

        const formatted = data.map((msg) => {
          const p = profileMap.get(msg.user_id);
          return {
            ...msg,
            user_name: p?.naam || 'Onbekend',
            avatar_url: p?.avatar_url ?? undefined,
          };
        });

        // Data comes newest-first, reverse for display
        const sorted = formatted.reverse();

        if (isInitial) {
          setMessages(sorted);
        } else {
          // Preserve scroll position when prepending older messages
          const container = scrollContainerRef.current;
          const prevScrollHeight = container?.scrollHeight ?? 0;
          setMessages((prev) => [...sorted, ...prev]);
          requestAnimationFrame(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - prevScrollHeight;
            }
          });
        }

        setHasMore(data.length === PAGE_SIZE);
      }
    } catch (error) {
      console.error('Error fetching samen messages:', error);
    } finally {
      if (isInitial) setLoading(false);
      else setLoadingOlder(false);
    }
  }, [samenId, meditationId]);

  // Initial fetch + realtime subscription
  useEffect(() => {
    if (!samenId) return;

    fetchMessages();

    const channel = supabase
      .channel(`samen_messages_${samenId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'samen_messages',
          filter: `samen_id=eq.${samenId}`,
        },
        async (payload) => {
          const row = payload.new as any;
          // Skip if meditation filter doesn't match
          if (meditationId && row.meditation_id !== meditationId) return;
          if (!meditationId && row.meditation_id) return;

          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('naam, avatar_url')
            .eq('id', row.user_id)
            .single();

          const msg: Message = {
            ...row,
            user_name: senderProfile?.naam || 'Onbekend',
            avatar_url: senderProfile?.avatar_url,
          };
          setMessages((prev) =>
            prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
          );
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'samen_messages',
          filter: `samen_id=eq.${samenId}`,
        },
        (payload) => {
          const deletedId = (payload.old as any).id;
          setMessages((prev) => prev.filter((m) => m.id !== deletedId));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [samenId, meditationId, fetchMessages]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!samenId || !newMessage.trim() || !profile) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      const { data, error } = await supabase
        .from('samen_messages')
        .insert({
          user_id: profile.id,
          samen_id: samenId,
          content,
          meditation_id: meditationId ?? null,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const msg: Message = {
          ...data,
          user_name: profile.naam ?? 'Onbekend',
          avatar_url: profile.avatar_url ?? undefined,
        };
        setMessages((prev) =>
          prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
        );
      }
    } catch (error) {
      console.error('Error sending samen message:', error);
      setNewMessage(content);
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('samen_messages')
        .delete()
        .eq('id', messageId);
      if (error) throw error;
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting samen message:', error);
    }
  };

  const loadOlder = () => {
    if (messages.length === 0 || loadingOlder) return;
    fetchMessages(messages[0].created_at);
  };

  return (
    <Card className="flex flex-col h-[500px] min-h-0 bg-white shadow-md border-green-500">
      <CardHeader className="bg-gradient-to-r from-green-500/10 to-blue-500/10 pb-2">
        <CardTitle className="flex items-center justify-between text-lg font-medium text-green-800">
          <div className="flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-green-600" />
            Samen Chat
          </div>
          {onlineCount != null && onlineCount > 0 && (
            <span className="text-xs font-normal text-muted-foreground flex items-center">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-1" />
              {onlineCount} online
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent ref={scrollContainerRef} className="flex-grow overflow-y-auto p-4">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-muted-foreground">Berichten laden...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {hasMore && messages.length > 0 && (
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadOlder}
                  disabled={loadingOlder}
                  className="text-muted-foreground"
                >
                  {loadingOlder ? 'Laden...' : 'Eerdere berichten laden'}
                </Button>
              </div>
            )}
            {messages.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <p className="text-muted-foreground">
                  Nog geen berichten. Start het gesprek!
                </p>
              </div>
            ) : (
              messages.map((message) => {
                const isOwn = message.user_id === profile?.id;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group/msg`}
                  >
                    <div
                      className={`flex ${isOwn ? 'flex-row-reverse' : 'flex-row'} max-w-[80%] sm:max-w-[34rem] gap-2`}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={message.avatar_url ?? undefined} alt={message.user_name} />
                        <AvatarFallback>{message.user_name?.substring(0, 2) || 'U'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div
                          className={`px-3 py-2 rounded-lg ${
                            isOwn
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {message.content}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <p className="text-xs text-muted-foreground">
                            {message.user_name} •{' '}
                            {new Date(message.created_at).toLocaleString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                          {isOwn ? (
                            <button
                              type="button"
                              onClick={() => deleteMessage(message.id)}
                              className="[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/msg:opacity-100 [@media(hover:hover)]:focus:opacity-100 transition-opacity text-muted-foreground hover:text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          ) : (
                            <ChatMessageMenu
                              messageId={message.id}
                              authorId={message.user_id}
                              authorName={message.user_name ?? 'Deze gebruiker'}
                              source="samen"
                              onBlocked={(blockedId) =>
                                setMessages((prev) =>
                                  prev.filter((m) => m.user_id !== blockedId),
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </CardContent>
      <CardFooter className="p-4 pt-2 border-t">
        <form onSubmit={sendMessage} className="flex w-full gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Typ een bericht..."
            className="flex-grow"
          />
          <Button type="submit" size="icon" disabled={!newMessage.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
}
